import {
  CalendarClockIcons,
  EmailIcons,
  GitHubIcons,
  LinkIcons,
  LinkedInIcons,
  MapPinIcons,
  PhoneIcons,
  WebsiteIcons,
} from '../../../../../icons/icons';
import { Address, SocialLinks, StatusHistoryItem } from '../../../../../utils/types/JobOpenings';
import { StatusHistoryTimeline } from '../../../../../ui/components/Timeline';
import { Box, Chip, Link, Paper, Stack, Typography } from '@mui/material';

interface IOverview {
  phone?: string;
  email?: string;
  address?: Address;
  aiScore?: number;
  applicantStatus?: string;
  notes?: string;
  socials?: SocialLinks;
  statusHistory?: StatusHistoryItem[];
  createdAt?: number | string;
}

const STATUS_STYLES: Record<string, { bg: string; color: string; dot: string }> = {
  Outstanding: { bg: '#dbeafe', color: '#1e40af', dot: '#3b82f6' },
  'Auto-Assessment': { bg: '#f3e8ff', color: '#6b21a8', dot: '#a855f7' },
  Shortlisted: { bg: '#dcfce7', color: '#166534', dot: '#22c55e' },
  Unsuccessful: { bg: '#fee2e2', color: '#991b1b', dot: '#ef4444' },
  Suitable: { bg: '#ccfbf1', color: '#115e59', dot: '#14b8a6' },
  New: { bg: '#fef9c3', color: '#854d0e', dot: '#eab308' },
  Completed: { bg: '#d1fae5', color: '#065f46', dot: '#10b981' },
};

const DEFAULT_STATUS_STYLE = { bg: '#f3f4f6', color: '#1f2937', dot: '#6b7280' };

export const OverviewTab = (props: IOverview) => {
  const { phone, email, address, applicantStatus, aiScore, socials, statusHistory, createdAt } = props;

  const statusStyle = STATUS_STYLES[applicantStatus || ''] || DEFAULT_STATUS_STYLE;

  return (
    <Stack spacing={3} sx={{ py: 1, animation: 'fadeIn 0.3s ease-in' }}>
      {/* Contact Info */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'grey.100',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            bgcolor: '#eff6ff',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'grey.100',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <PhoneIcons size={16} />
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1d4ed8' }}>
              Contact Information
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ '& > *:not(:last-child)': { borderBottom: '1px solid', borderColor: 'grey.100' } }}>
          {/* Phone */}
          <Stack direction="row" alignItems="flex-start" sx={{ px: 2, py: 1.5 }}>
            <Box sx={{ flexShrink: 0, color: '#3b82f6', mr: 1.5, mt: 0.5 }}>
              <PhoneIcons size={20} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '11px', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                Phone
              </Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.primary' }}>
                {phone}
              </Typography>
            </Box>
          </Stack>

          {/* Email */}
          <Stack direction="row" alignItems="flex-start" sx={{ px: 2, py: 1.5 }}>
            <Box sx={{ flexShrink: 0, color: '#3b82f6', mr: 1.5, mt: 0.5 }}>
              <EmailIcons size={20} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '11px', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                Email
              </Typography>
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.primary', wordBreak: 'break-all' }}>
                {email}
              </Typography>
            </Box>
          </Stack>

          {/* Address */}
          {address && (
            <Stack direction="row" alignItems="flex-start" sx={{ px: 2, py: 1.5 }}>
              <Box sx={{ flexShrink: 0, color: '#3b82f6', mr: 1.5, mt: 0.5 }}>
                <MapPinIcons size={20} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '11px', fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                  Address
                </Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 500, color: 'text.primary' }}>
                  {[address.street, address.city, address.state, address.country]
                    .filter(Boolean)
                    .join(', ')}
                </Typography>
              </Box>
            </Stack>
          )}
        </Box>
      </Paper>

      {/* Status */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'grey.100',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            bgcolor: '#f0fdf4',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'grey.100',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarClockIcons size={16} />
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#15803d' }}>
              Application Status
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ p: 2 }}>
          <Stack direction="row" flexWrap="wrap" spacing={1.5}>
            <Chip
              size="small"
              label={
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: statusStyle.dot,
                    }}
                  />
                  <span>{applicantStatus}</span>
                </Stack>
              }
              sx={{
                bgcolor: statusStyle.bg,
                color: statusStyle.color,
                fontWeight: 500,
                fontSize: '12px',
                height: 28,
                borderRadius: '14px',
              }}
            />

            {aiScore && (
              <Chip
                size="small"
                icon={<LinkIcons size={14} />}
                label={`CV Score: ${aiScore}%`}
                sx={{
                  bgcolor: '#faf5ff',
                  color: '#7e22ce',
                  fontWeight: 500,
                  fontSize: '12px',
                  height: 28,
                  borderRadius: '14px',
                }}
              />
            )}
          </Stack>
        </Box>
      </Paper>

      {/* Status History Timeline */}
      <StatusHistoryTimeline
        statusHistory={statusHistory}
        currentStatus={applicantStatus}
        createdAt={createdAt}
        title="Application Timeline"
      />

      {/* Social Links */}
      {socials &&
        (socials.github || socials.linkedin || socials.personalWebsite) && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'grey.100',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                bgcolor: '#faf5ff',
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'grey.100',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <LinkIcons size={16} />
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#7e22ce' }}>
                  Social Links
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ p: 2 }}>
              <Stack direction="row" flexWrap="wrap" spacing={2}>
                {socials?.github && (
                  <Link
                    href={
                      socials.github.startsWith('http')
                        ? socials.github
                        : `https://${socials.github}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.5,
                      py: 1,
                      bgcolor: '#f3f4f6',
                      borderRadius: '8px',
                      color: '#1f2937',
                      transition: 'all 0.2s',
                      '&:hover': { bgcolor: '#e5e7eb' },
                    }}
                  >
                    <GitHubIcons size={20} />
                    <Typography sx={{ ml: 1, fontSize: '12px', fontWeight: 500 }}>
                      GitHub
                    </Typography>
                  </Link>
                )}

                {socials?.linkedin && (
                  <Link
                    href={
                      socials.linkedin.startsWith('http')
                        ? socials.linkedin
                        : `https://${socials.linkedin}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.5,
                      py: 1,
                      bgcolor: '#dbeafe',
                      borderRadius: '8px',
                      color: '#1e40af',
                      transition: 'all 0.2s',
                      '&:hover': { bgcolor: '#bfdbfe' },
                    }}
                  >
                    <LinkedInIcons size={20} />
                    <Typography sx={{ ml: 1, fontSize: '12px', fontWeight: 500 }}>
                      LinkedIn
                    </Typography>
                  </Link>
                )}

                {socials?.personalWebsite && (
                  <Link
                    href={
                      socials.personalWebsite.startsWith('http')
                        ? socials.personalWebsite
                        : `https://${socials.personalWebsite}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.5,
                      py: 1,
                      bgcolor: '#dcfce7',
                      borderRadius: '8px',
                      color: '#166534',
                      transition: 'all 0.2s',
                      '&:hover': { bgcolor: '#bbf7d0' },
                    }}
                  >
                    <WebsiteIcons size={20} />
                    <Typography sx={{ ml: 1, fontSize: '12px', fontWeight: 500 }}>
                      Website
                    </Typography>
                  </Link>
                )}
              </Stack>
            </Box>
          </Paper>
        )}
    </Stack>
  );
};
